import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router";
import { useData } from "@/context/data-provider";
import { calcScore, calcCompliance, isAtRisk } from "@/lib/scoring";
import { todayKey } from "@/lib/constants";
import type { Doctor, Priority } from "@/types";
import { DoctorDetailModal } from "@/components/shared/doctor-detail-modal";

function vnumStyle(n: number): React.CSSProperties {
  if (n === 1) return { background: "linear-gradient(135deg,#4A9E2F,#5BAD3E)", color: "#fff" };
  if (n === 2) return { background: "linear-gradient(135deg,#1B4F9B,#2E7BC4)", color: "#fff" };
  return { background: "linear-gradient(135deg,#6C3483,#8E44AD)", color: "#fff" };
}

function scoreClass(s: number) {
  if (s >= 70) return "score-green";
  if (s >= 40) return "score-orange";
  return "score-red";
}

export default function Dashboard() {
  const {
    doctors: { doctors },
    visits: { visits, todayVisits, addVisit, resetDay },
    period: { period },
    plans: { plans, markDone },
    products: { products },
    apoios: { apoios },
  } = useData();
  const navigate = useNavigate();

  const [detailDoc, setDetailDoc] = useState<Doctor | null>(null);
  const [doctorSearch, setDoctorSearch] = useState("");
  const [selectedDoctorId, setSelectedDoctorId] = useState<string | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [comment, setComment] = useState("");
  const [acOpen, setAcOpen] = useState(false);

  const stats = useMemo(() => {
    const visitedToday = new Set(todayVisits.map((v) => v.doctorId)).size;
    const planDone = plans.filter((p) => p.done).length;
    const compliant = doctors.filter(
      (d) => calcCompliance(d, visits, period).status === "ok"
    ).length;
    const atRisk = doctors.filter((d) => isAtRisk(d, visits, period)).length;
    const avgScore =
      doctors.length > 0
        ? Math.round(doctors.reduce((sum, d) => sum + calcScore(d, visits, period), 0) / doctors.length)
        : 0;
    const compPct = doctors.length > 0 ? Math.round((compliant / doctors.length) * 100) : 0;
    return { total: doctors.length, visitedToday, planDone, planTotal: plans.length, compliant, atRisk, avgScore, compPct };
  }, [doctors, visits, todayVisits, period, plans]);

  const acResults = useMemo(() => {
    if (!doctorSearch.trim()) return [];
    const q = doctorSearch.toLowerCase();
    return doctors.filter(
      (d) => d.name.toLowerCase().includes(q) || d.local.toLowerCase().includes(q) || d.espec.toLowerCase().includes(q)
    ).slice(0, 8);
  }, [doctorSearch, doctors]);

  const daysRemaining = Math.max(0, Math.ceil((new Date(period.end).getTime() - Date.now()) / 86400000));

  const handleSelectDoctor = (id: string) => {
    const doc = doctors.find((d) => d.id === id);
    if (doc) {
      setSelectedDoctorId(id);
      setDoctorSearch(doc.name);
      setAcOpen(false);
    }
  };

  const handleRegister = () => {
    const doc = doctors.find((d) => d.id === selectedDoctorId);
    if (!doc) return;
    const doctorVisits = Object.values(visits).flat().filter((v) => v.doctorId === doc.id);
    addVisit({
      doctorId: doc.id,
      name: doc.name,
      comment,
      visitNumber: doctorVisits.length + 1,
      products: selectedProducts,
    });
    markDone(doc.id);
    setDoctorSearch("");
    setSelectedDoctorId(null);
    setSelectedProducts([]);
    setComment("");
  };

  const riskyDocs = useMemo(
    () => doctors.filter((d) => isAtRisk(d, visits, period)),
    [doctors, visits, period]
  );

  return (
    <div className="container">
      {/* Stats Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-[10px] mb-[18px]">
        {[
          { cls: "sc-total", label: "Total Médicos", value: stats.total, sub: "no painel" },
          { cls: "sc-visited", label: "Visitados Hoje", value: stats.visitedToday, sub: "únicos" },
          { cls: "sc-plan", label: "Plano Hoje", value: `${stats.planDone}/${stats.planTotal}`, sub: "realizados" },
          { cls: "sc-ok", label: "Dentro Parâmetros", value: stats.compliant, sub: "no período", bar: stats.compPct },
          { cls: "sc-nok", label: "Em Risco", value: stats.atRisk, sub: "precisam atenção" },
          { cls: "sc-score", label: "Score Médio", value: stats.avgScore || "—", sub: "relacionamento" },
        ].map((s) => (
          <div key={s.cls} className={`stat-card ${s.cls}`}>
            <div className="stat-label">{s.label}</div>
            <div className="stat-number">{s.value}</div>
            <div className="stat-sub">{s.sub}</div>
            {s.bar !== undefined && (
              <div className="mt-[6px] h-[3px] bg-[var(--mv-surface2)] rounded-sm overflow-hidden">
                <div
                  className="h-full rounded-sm transition-all duration-600"
                  style={{ width: `${s.bar}%`, background: "linear-gradient(90deg,#4A9E2F,#5BAD3E)" }}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Period Banner */}
      <div className="period-banner mb-4">
        <span className="font-mono text-[0.58rem] uppercase tracking-widest" style={{ color: "var(--mv-ink-light)" }}>
          Período
        </span>
        <span className="font-serif text-[0.95rem]" style={{ color: "var(--mv-ink-dark)" }}>
          {period.start} — {period.end}
        </span>
        <span className="font-mono text-[0.68rem] ml-auto" style={{ color: "var(--mv-ink-light)" }}>
          {daysRemaining} dias restantes
        </span>
        <button className="preset-btn" onClick={() => navigate("/periodo")}>
          ✏ Alterar
        </button>
      </div>

      {/* Alerts */}
      {riskyDocs.length > 0 && (
        <div
          className="rounded-[10px] p-[10px_16px] mb-4 flex items-start gap-3 flex-wrap"
          style={{ background: "linear-gradient(135deg,#FDF0E3,#FDECEA)", border: "1px solid #F1948A" }}
        >
          <span className="text-base flex-shrink-0">⚠️</span>
          <div className="flex flex-wrap gap-2">
            {riskyDocs.slice(0, 5).map((d) => (
              <span key={d.id} className="text-[0.78rem] flex items-center gap-1" style={{ color: "var(--mv-ink-dark)" }}>
                <span className={`prio-dot prio-dot-${d.prio}`} />
                {d.name}
              </span>
            ))}
            {riskyDocs.length > 5 && (
              <span className="text-[0.78rem]" style={{ color: "var(--mv-ink-light)" }}>
                +{riskyDocs.length - 5} mais
              </span>
            )}
          </div>
        </div>
      )}

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_370px] gap-[18px]">
        {/* Left: Visit Registration + Log */}
        <div className="space-y-[18px]">
          {/* Register Visit */}
          <div className="mv-panel">
            <div className="mv-panel-header">
              <span className="mv-panel-title">Registar Visita</span>
            </div>
            <div className="p-[14px_18px]">
              <div className="flex gap-2 mb-[9px]">
                <div className="relative flex-1">
                  <input
                    className="mv-input"
                    placeholder="Nome do médico..."
                    value={doctorSearch}
                    onChange={(e) => {
                      setDoctorSearch(e.target.value);
                      setSelectedDoctorId(null);
                      setAcOpen(true);
                    }}
                    onFocus={() => setAcOpen(true)}
                    onBlur={() => setTimeout(() => setAcOpen(false), 200)}
                  />
                  {acOpen && acResults.length > 0 && (
                    <div
                      className="absolute top-[calc(100%+3px)] left-0 right-0 z-[200] rounded-[9px] max-h-[240px] overflow-y-auto"
                      style={{
                        background: "var(--mv-surface)",
                        border: "1.5px solid var(--mv-blue)",
                        boxShadow: "0 8px 24px var(--mv-shadow)",
                      }}
                    >
                      {acResults.map((d) => (
                        <div
                          key={d.id}
                          className="p-[9px_12px] cursor-pointer flex items-center justify-between gap-2 transition-colors hover:bg-[var(--mv-blue-light)]"
                          style={{ borderBottom: "1px solid var(--mv-border)" }}
                          onMouseDown={() => handleSelectDoctor(d.id)}
                        >
                          <div>
                            <div className="font-semibold text-[0.82rem]" style={{ color: "var(--mv-ink-dark)" }}>{d.name}</div>
                            <div className="text-[0.67rem]" style={{ color: "var(--mv-ink-light)" }}>{d.local}</div>
                          </div>
                          <div className="flex flex-col items-end gap-[3px]">
                            <span className={`ptag ptag-${d.prio}`}>{d.prio}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button className="mv-btn mv-btn-primary" onClick={handleRegister} disabled={!selectedDoctorId}>
                  Registar
                </button>
              </div>

              <textarea
                className="mv-textarea mb-2"
                placeholder="Comentário sobre a visita (opcional)..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={2}
              />

              {products.length > 0 && (
                <div className="mb-[6px]">
                  <div className="mv-label mb-[6px]">Produtos / Temas abordados</div>
                  <div className="flex flex-wrap gap-[6px]">
                    {products.map((p) => (
                      <button
                        key={p}
                        className={`prod-chip ${selectedProducts.includes(p) ? "selected" : ""}`}
                        onClick={() =>
                          setSelectedProducts((prev) =>
                            prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
                          )
                        }
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Visit Log */}
          <div className="mv-panel">
            <div className="mv-panel-header">
              <span className="mv-panel-title">Visitas de Hoje</span>
              <span className="mv-badge">{todayVisits.length}</span>
              {todayVisits.length > 0 && (
                <button className="mv-btn mv-btn-danger mv-btn-sm ml-auto" onClick={resetDay}>Limpar dia</button>
              )}
            </div>
            <div className="max-h-[320px] overflow-y-auto">
              {todayVisits.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📋</div>
                  Ainda não registou visitas hoje.
                </div>
              ) : (
                [...todayVisits].reverse().map((v, i) => (
                  <div key={i} className="visit-item">
                    <div className="vnum" style={vnumStyle(v.visitNumber)}>
                      {v.visitNumber}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[0.83rem]" style={{ color: "var(--mv-ink-dark)" }}>{v.name}</div>
                      {v.comment && (
                        <div className="text-[0.73rem] leading-[1.4] mt-[2px]" style={{ color: "var(--mv-ink-light)" }}>{v.comment}</div>
                      )}
                      <div className="font-mono text-[0.57rem] mt-[3px] flex gap-[7px] items-center flex-wrap" style={{ color: "var(--mv-ink-light)" }}>
                        <span>{v.time}</span>
                      </div>
                      {v.products.length > 0 && (
                        <div className="flex gap-1 flex-wrap mt-1">
                          {v.products.map((p) => (
                            <span key={p} className="prod-tag">{p}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Right: Doctor Sidebar */}
        <div className="mv-panel self-start">
          <div className="mv-panel-header">
            <span className="mv-panel-title">Médicos</span>
            <span className="mv-badge">{doctors.length}</span>
            <button className="mv-btn mv-btn-primary mv-btn-sm ml-auto" onClick={() => navigate("/medicos")}>
              + Novo
            </button>
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {doctors.slice(0, 15).map((d) => {
              const score = calcScore(d, visits, period);
              return (
                <div key={d.id} className="doctor-item" onClick={() => setDetailDoc(d)}>
                  <div className={`prio-dot prio-dot-${d.prio}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[0.81rem] truncate" style={{ color: "var(--mv-ink-dark)" }}>{d.name}</div>
                    <div className="text-[0.67rem] truncate" style={{ color: "var(--mv-ink-light)" }}>{d.local}</div>
                  </div>
                  <div className={`score-circle ${scoreClass(score)}`}>{score}</div>
                </div>
              );
            })}
          </div>
          <div className="p-[8px_13px]" style={{ borderTop: "1px solid var(--mv-border)" }}>
            <button className="mv-btn mv-btn-ghost mv-btn-sm w-full" onClick={() => navigate("/medicos")}>
              Ver lista completa →
            </button>
          </div>
        </div>
      </div>

      {detailDoc && (
        <DoctorDetailModal
          doctor={detailDoc}
          visits={visits}
          period={period}
          apoios={apoios}
          onClose={() => setDetailDoc(null)}
          onEdit={() => { setDetailDoc(null); navigate("/medicos"); }}
        />
      )}
    </div>
  );
}
